<template>
  <div class="insuring-page">
    <div class="page-header">
      <PageHeaderPortal>
<div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-file-invoice-dollar"></i>
        </div>

        <div>
          <h1 class="page-title">Insuring</h1>
          <p class="page-subtitle">Upload FHA MIP payments for processing</p>
        </div>
      </div>
      </PageHeaderPortal>
    </div>

    <!-- ════ UFMIP payment receipts (unchanged flow) ════ -->
    <div class="upload-card">
      <div class="section-header">
        <i class="fa-duotone fa-file-pdf section-icon"></i>
        <div>
          <div class="section-title">UFMIP Payment Receipts</div>
          <div class="section-sub">Up-front premium receipt PDFs</div>
        </div>
      </div>

      <div
        class="dropzone"
        :class="{ 'dropzone-active': isDragging }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="onDrop"
        @click="openFilePicker"
      >
        <i class="fa-duotone fa-cloud-arrow-up dropzone-icon"></i>
        <div class="dropzone-text">
          <strong>Click to browse</strong> or drag PDF receipts here
        </div>
        <div class="dropzone-hint">You can select multiple files at once</div>
        <input
          ref="fileInput"
          type="file"
          accept="application/pdf"
          multiple
          class="hidden-input"
          @change="onFilesSelected"
        />
      </div>

      <div v-if="pendingFiles.length" class="pending-list">
        <div class="pending-list-header">
          <span>{{ pendingFiles.length }} file{{ pendingFiles.length === 1 ? '' : 's' }} ready to upload</span>
          <button class="clear-btn" @click="clearPending" :disabled="uploading">Clear</button>
        </div>

        <div class="pending-item" v-for="(file, idx) in pendingFiles" :key="idx">
          <i class="fa-duotone fa-file-pdf pending-icon"></i>
          <span class="pending-name">{{ file.name }}</span>
          <span class="pending-size">{{ formatSize(file.size) }}</span>
          <button class="remove-btn" @click="removeFile(idx)" :disabled="uploading">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <div class="upload-actions">
        <button
          class="upload-btn"
          :disabled="!pendingFiles.length || uploading"
          @click="uploadFiles"
        >
          {{ uploading ? 'Uploading...' : `Upload ${pendingFiles.length || ''} Receipt${pendingFiles.length === 1 ? '' : 's'}` }}
        </button>
      </div>

      <div v-if="errorMessage" class="banner banner-error">{{ errorMessage }}</div>
    </div>

    <!-- UFMIP results -->
    <div v-if="results.length" class="table-card">
      <div class="table-card-header">
        <div class="table-card-title-wrap">
          <i class="fa-duotone fa-clipboard-list-check table-card-icon"></i>
          <span class="table-card-title">UFMIP Upload Results</span>
        </div>
        <div class="table-card-count">{{ results.length }}</div>
      </div>

      <div class="table-wrap">
        <table class="results-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Status</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, idx) in results" :key="idx">
              <td class="primary-text">{{ r.fileName }}</td>
              <td>
                <span class="status-pill" :class="r.success ? 'status-success' : 'status-failed'">
                  {{ r.success ? 'Uploaded' : 'Failed' }}
                </span>
              </td>
              <td>{{ r.success ? 'Queued for processing' : (r.error || 'Unknown error') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ════ Monthly FHA MIP (periodic premiums) — paired wizard ════ -->
    <div class="upload-card">
      <div class="section-header">
        <i class="fa-duotone fa-calendar-days section-icon section-icon-monthly"></i>
        <div>
          <div class="section-title">Monthly MIP (Periodic Premiums)</div>
          <div class="section-sub">Each batch needs BOTH files: the FHAC Excel export and its matching receipt PDF</div>
        </div>
      </div>

      <!-- wizard prompt -->
      <div class="wizard-banner" :class="monthlyDraft.excel ? 'wizard-banner-pdf' : 'wizard-banner-excel'">
        <i class="fa-duotone" :class="monthlyDraft.excel ? 'fa-file-pdf' : 'fa-file-excel'"></i>
        <span v-if="!monthlyDraft.excel">
          <strong>Step 1 of 2:</strong> add the FHA Excel Export (.xls / .xlsx) for this batch
        </span>
        <span v-else>
          <strong>Step 2 of 2:</strong> now add the receipt PDF that matches
          <strong>{{ monthlyDraft.excel.name }}</strong>
        </span>
      </div>

      <div
        class="dropzone"
        :class="{ 'dropzone-active': monthlyDragging }"
        @dragover.prevent="monthlyDragging = true"
        @dragleave.prevent="monthlyDragging = false"
        @drop.prevent="onMonthlyDrop"
        @click="openMonthlyFilePicker"
      >
        <i class="fa-duotone dropzone-icon" :class="monthlyDraft.excel ? 'fa-file-pdf' : 'fa-file-excel'"></i>
        <div class="dropzone-text">
          <strong>Click to browse</strong> or drag the
          {{ monthlyDraft.excel ? 'matching receipt PDF' : 'FHA Excel Export' }} here
        </div>
        <div class="dropzone-hint">
          {{ monthlyDraft.excel ? 'PDF only — the receipt for the export above' : '.xls / .xlsx — one export per batch' }}
        </div>
        <input
          ref="monthlyFileInput"
          type="file"
          :accept="monthlyDraft.excel ? 'application/pdf,.pdf' : '.xls,.xlsx'"
          class="hidden-input"
          @change="onMonthlyFilesSelected"
        />
      </div>

      <!-- completed pairs + in-progress draft -->
      <div v-if="monthlyPairs.length || monthlyDraft.excel" class="pending-list">
        <div class="pending-list-header">
          <span>
            {{ monthlyPairs.length }} complete batch{{ monthlyPairs.length === 1 ? '' : 'es' }}
            <template v-if="monthlyDraft.excel"> — 1 awaiting its receipt PDF</template>
          </span>
          <button class="clear-btn" @click="clearMonthlyPending" :disabled="monthlyUploading">Clear all</button>
        </div>

        <div class="pair-row" v-for="(pair, idx) in monthlyPairs" :key="'pair-' + idx">
          <div class="pair-file">
            <i class="fa-duotone fa-file-excel pending-icon pending-icon-excel"></i>
            <span class="pending-name">{{ pair.excel.name }}</span>
            <span class="pending-size">{{ formatSize(pair.excel.size) }}</span>
          </div>
          <i class="fa-solid fa-link pair-link"></i>
          <div class="pair-file">
            <i class="fa-duotone fa-file-pdf pending-icon"></i>
            <span class="pending-name">{{ pair.pdf.name }}</span>
            <span class="pending-size">{{ formatSize(pair.pdf.size) }}</span>
          </div>
          <button class="remove-btn" @click="removeMonthlyPair(idx)" :disabled="monthlyUploading">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="pair-row pair-row-draft" v-if="monthlyDraft.excel">
          <div class="pair-file">
            <i class="fa-duotone fa-file-excel pending-icon pending-icon-excel"></i>
            <span class="pending-name">{{ monthlyDraft.excel.name }}</span>
            <span class="pending-size">{{ formatSize(monthlyDraft.excel.size) }}</span>
          </div>
          <i class="fa-solid fa-link pair-link"></i>
          <div class="pair-file pair-file-missing">
            <i class="fa-duotone fa-file-pdf pending-icon"></i>
            <span class="pending-name">awaiting receipt PDF…</span>
          </div>
          <button class="remove-btn" @click="cancelMonthlyDraft" :disabled="monthlyUploading">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <div class="upload-actions">
        <button
          class="upload-btn"
          :disabled="!monthlyPairs.length || !!monthlyDraft.excel || monthlyUploading"
          @click="uploadMonthlyFiles"
        >
          {{ monthlyUploading
            ? 'Uploading...'
            : monthlyDraft.excel
              ? 'Finish the pair to upload'
              : `Upload ${monthlyPairs.length || ''} Batch${monthlyPairs.length === 1 ? '' : 'es'}` }}
        </button>
      </div>

      <div v-if="monthlyErrorMessage" class="banner banner-error">{{ monthlyErrorMessage }}</div>
    </div>

    <!-- Monthly results -->
    <div v-if="monthlyResults.length" class="table-card">
      <div class="table-card-header">
        <div class="table-card-title-wrap">
          <i class="fa-duotone fa-clipboard-list-check table-card-icon"></i>
          <span class="table-card-title">Monthly MIP Upload Results</span>
        </div>
        <div class="table-card-count">{{ monthlyResults.length }}</div>
      </div>

      <div class="table-wrap">
        <table class="results-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Status</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, idx) in monthlyResults" :key="idx">
              <td class="primary-text">{{ r.fileName }}</td>
              <td>
                <span class="status-pill" :class="r.success ? 'status-success' : 'status-failed'">
                  {{ r.success ? 'Uploaded' : 'Failed' }}
                </span>
              </td>
              <td>{{ r.success ? 'Staged for next monthly sweep' : (r.error || 'Unknown error') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
import api from '@/api/api.service'
import auth from '@/authConfig.js'

export default {
  components: { PageHeaderPortal },
  name: 'InsuringView',
  data() {
    return {
      // UFMIP receipts
      pendingFiles: [],
      isDragging: false,
      uploading: false,
      errorMessage: '',
      results: [],

      // Monthly MIP — pair wizard: every batch is { excel, pdf }
      monthlyPairs: [],
      monthlyDraft: { excel: null, pdf: null },
      monthlyDragging: false,
      monthlyUploading: false,
      monthlyErrorMessage: '',
      monthlyResults: []
    }
  },
  methods: {
    // ── shared ────────────────────────────────────────────────────────────
    formatSize(bytes) {
      if (!bytes) return '0 KB'
      const kb = bytes / 1024
      if (kb < 1024) return `${kb.toFixed(0)} KB`
      return `${(kb / 1024).toFixed(1)} MB`
    },
    isExcel(name) {
      const n = (name || '').toLowerCase()
      return n.endsWith('.xls') || n.endsWith('.xlsx')
    },
    isPdf(file) {
      return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    },

    // ── UFMIP receipts (unchanged behavior) ───────────────────────────────
    openFilePicker() {
      this.$refs.fileInput.click()
    },
    onFilesSelected(e) {
      this.addFiles(Array.from(e.target.files || []))
      e.target.value = ''
    },
    onDrop(e) {
      this.isDragging = false
      this.addFiles(Array.from(e.dataTransfer?.files || []))
    },
    addFiles(files) {
      const pdfs = files.filter(f => this.isPdf(f))
      if (pdfs.length !== files.length) {
        this.errorMessage = 'Only PDF files are accepted — non-PDF files were skipped.'
      } else {
        this.errorMessage = ''
      }
      this.pendingFiles.push(...pdfs)
    },
    removeFile(idx) {
      this.pendingFiles.splice(idx, 1)
    },
    clearPending() {
      this.pendingFiles = []
    },
    async uploadFiles() {
      if (!this.pendingFiles.length) return

      this.uploading = true
      this.errorMessage = ''

      try {
        const tokenResponse = await auth.acquireTokenResponse()
        const token = tokenResponse.accessToken

        const response = await api.UploadFhaMipReceipts(this.pendingFiles, token)
        const batch = response?.data?.data

        this.results = batch?.results || []
        this.pendingFiles = []

        if (!response?.data?.isSuccess) {
          this.errorMessage = response?.data?.message || 'One or more receipts failed to upload.'
        }
      } catch (e) {
        this.errorMessage =
          e?.response?.data?.message ||
          e?.message ||
          'Failed to upload receipts.'
      } finally {
        this.uploading = false
      }
    },

    // ── Monthly MIP — pair wizard ────────────────────────────────
    openMonthlyFilePicker() {
      this.$refs.monthlyFileInput.click()
    },
    onMonthlyFilesSelected(e) {
      this.addMonthlyFiles(Array.from(e.target.files || []))
      e.target.value = ''
    },
    onMonthlyDrop(e) {
      this.monthlyDragging = false
      this.addMonthlyFiles(Array.from(e.dataTransfer?.files || []))
    },
    // Wizard gate: files are only accepted in pair order — Excel first,
    // then the matching PDF. Anything out of order is rejected with a
    // message instead of silently queuing, so a batch can never go up
    // without its receipt.
    addMonthlyFiles(files) {
      this.monthlyErrorMessage = ''
      for (const f of files) {
        if (!this.monthlyDraft.excel) {
          if (this.isExcel(f.name)) {
            this.monthlyDraft.excel = f
          } else if (this.isPdf(f)) {
            this.monthlyErrorMessage = 'Start each batch with its FHA Excel Export — add the .xls/.xlsx first, then its receipt PDF.'
          } else {
            this.monthlyErrorMessage = `“${f.name}” isn't an .xls/.xlsx export or a PDF receipt.`
          }
        } else {
          if (this.isPdf(f)) {
            this.monthlyPairs.push({ excel: this.monthlyDraft.excel, pdf: f })
            this.monthlyDraft = { excel: null, pdf: null }
          } else if (this.isExcel(f.name)) {
            this.monthlyErrorMessage = `Finish the current batch first — “${this.monthlyDraft.excel.name}” still needs its receipt PDF before another export can be added.`
          } else {
            this.monthlyErrorMessage = `“${f.name}” isn't a PDF — the receipt for “${this.monthlyDraft.excel.name}” must be a PDF.`
          }
        }
      }
    },
    removeMonthlyPair(idx) {
      this.monthlyPairs.splice(idx, 1)
    },
    cancelMonthlyDraft() {
      this.monthlyDraft = { excel: null, pdf: null }
    },
    clearMonthlyPending() {
      this.monthlyPairs = []
      this.monthlyDraft = { excel: null, pdf: null }
      this.monthlyErrorMessage = ''
    },
    async uploadMonthlyFiles() {
      if (!this.monthlyPairs.length || this.monthlyDraft.excel) return

      this.monthlyUploading = true
      this.monthlyErrorMessage = ''

      try {
        const tokenResponse = await auth.acquireTokenResponse()
        const token = tokenResponse.accessToken

        // Flatten pairs — the backend pairs by HUD tracking id; the wizard
        // just guarantees every export ships with a receipt.
        const files = this.monthlyPairs.flatMap(p => [p.excel, p.pdf])

        const response = await api.UploadFhaMonthlyMipFiles(files, token)
        const batch = response?.data?.data

        this.monthlyResults = batch?.results || []
        this.monthlyPairs = []

        if (!response?.data?.isSuccess) {
          this.monthlyErrorMessage = response?.data?.message || 'One or more files failed to upload.'
        }
      } catch (e) {
        this.monthlyErrorMessage =
          e?.response?.data?.message ||
          e?.message ||
          'Failed to upload files.'
      } finally {
        this.monthlyUploading = false
      }
    }
  }
}
</script>

<style scoped>
.insuring-page {
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 100%;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-header-left { display: flex; align-items: center; gap: 14px; }

.page-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #eef2fb;
  color: #3d5fc7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.page-title { margin: 0; font-size: 20px; font-weight: 800; color: #1f2a44; }
.page-subtitle { margin: 2px 0 0; font-size: 13px; color: #7b8397; }

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #fdf0f2;
  color: #b24553;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}

.section-icon-monthly { background: #eef7f0; color: #2c7a4b; }

.section-title { font-size: 14px; font-weight: 800; color: #28344f; }
.section-sub { font-size: 12px; color: #7b8397; margin-top: 1px; }

.upload-card {
  background: #fbfbfd;
  border: 1px solid #e7ebf2;
  border-radius: 5px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.dropzone {
  border: 2px dashed #d9dfeb;
  border-radius: 8px;
  background: #fcfcfe;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.dropzone:hover,
.dropzone-active {
  border-color: #7c9ae0;
  background: #f5f8fe;
}

.dropzone-icon { font-size: 30px; color: #7c8db3; margin-bottom: 10px; display: block; }
.dropzone-text { font-size: 14px; color: #3b4a6b; }
.dropzone-hint { font-size: 12px; color: #97a2b8; margin-top: 4px; }
.hidden-input { display: none; }

.pending-list {
  border: 1px solid #e9edf3;
  border-radius: 6px;
  overflow: hidden;
}

.pending-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f7f9fc;
  font-size: 13px;
  font-weight: 700;
  color: #4a5773;
}

.pending-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-top: 1px solid #edf0f5;
  font-size: 13px;
}

.pending-icon { color: #b24553; }
.pending-icon-excel { color: #2c7a4b; }
.pending-name { flex: 1; color: #24314f; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pending-size { color: #7b8397; font-size: 12px; white-space: nowrap; }

.remove-btn,
.clear-btn {
  border: none;
  background: transparent;
  color: #97a2b8;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 8px;
}

.remove-btn:hover,
.clear-btn:hover { color: #b24553; }
.remove-btn:disabled,
.clear-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.upload-actions { display: flex; justify-content: flex-end; }

.upload-btn {
  border: 1px solid #315db5;
  border-radius: 5px;
  background: #315db5;
  color: #fff;
  height: 40px;
  padding: 0 20px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease;
}

.upload-btn:hover:not(:disabled) { background: #274a94; }
.upload-btn:disabled { opacity: 0.55; cursor: not-allowed; }

.banner {
  border-radius: 5px;
  padding: 10px 14px;
  font-size: 13px;
}

.banner-error { background: #fff0f1; border: 1px solid #f0d1d5; color: #b24553; }

.table-card {
  background: #fbfbfd;
  border: 1px solid #e7ebf2;
  border-radius: 5px;
  overflow: hidden;
}

.table-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #e9edf3;
  background: #fafbfd;
}

.table-card-title-wrap { display: flex; align-items: center; gap: 8px; }
.table-card-icon { color: #5f79c7; font-size: 14px; }
.table-card-title { font-size: 14px; font-weight: 700; color: #28344f; }
.table-card-count { font-size: 13px; font-weight: 700; color: #7b8397; }

.table-wrap { overflow: auto; }

.results-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.results-table th,
.results-table td {
  padding: 12px;
  border-bottom: 1px solid #edf0f5;
  font-size: 13px;
  text-align: left;
  vertical-align: middle;
}

.results-table th {
  background: #f7f9fc;
  color: #6f7b92;
  font-weight: 700;
}

.primary-text { font-weight: 700; color: #24314f; }

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid transparent;
}

.status-success { background: #eef9f0; border-color: #d9eedf; color: #2c7a4b; }
.status-failed { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }

@media (max-width: 700px) {
  .insuring-page { padding: 16px; }
}

/* ── Monthly MIP pair wizard ── */
.wizard-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 12px;
}
.wizard-banner-excel { background: #e9f7ef; color: #1e7a45; border: 1px solid #bfe6cf; }
.wizard-banner-pdf { background: #fdf0e7; color: #b35a1f; border: 1px solid #f3d5bd; }

.pair-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e6e9f2;
  border-radius: 8px;
  margin-top: 6px;
  background: #fff;
}
.pair-row-draft { border-style: dashed; background: #fffdf5; }
.pair-file { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
.pair-file-missing { color: #b35a1f; font-style: italic; }
.pair-link { color: #9aa3b8; font-size: 12px; }
.pending-icon-excel { color: #1e7a45; }
</style>
